import { Divider, Menu, MenuItem } from '@material-ui/core';
import { Lock, MoreHoriz } from '@material-ui/icons';
import { useEventHandler } from '@superdispatch/hooks';
import { Inline, useResponsiveValue } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import { ReactNode, useMemo } from 'react';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { PermissionTooltip } from 'shared/modules/permission/PermissionTooltip';
import {
  FeatureToggle,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { isFlagEnabled, useFlag } from 'shared/settings/FeatureToggles';
import { MenuButton } from 'shared/ui/MenuButton';
import { formatLoadStage, formatLoadStatus, LoadDTO } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadsPageContext } from './data/LoadsPageParamsContext';
import {
  LoadsPageLoadAction,
  LoadsPageLoadActionProps,
  actions,
} from './data/LoadsActions';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { useNavigate } from 'react-router-dom';

function isHTMLElement(element: unknown): element is HTMLElement {
  try {
    return element instanceof HTMLElement;
  } catch {
    return typeof element === 'object' && !!element && 'tagName' in element;
  }
}

interface LoadsPageLoadActionsProps {
  load: LoadDTO;
  onAction: (action: LoadsPageLoadAction) => void;
}

export function LoadsPageLoadActions({
  load,
  onAction,
}: LoadsPageLoadActionsProps) {
  const navigate = useNavigate();
  const { data: settings } = useCarrierSettings();
  const { params } = useLoadsPageContext();
  const handleAction = useEventHandler(onAction);
  const shouldShowOptions = useFlag('ctms_loads_options');
  const isMobile = useResponsiveValue(true, false, false);
  const hasNotManageAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_manage',
  });
  const hasNotMarkAsPaidAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_mark_as_paid',
  });
  const hasNoSendInvoiceAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_send_invoice',
  });
  const hasNoSendBOLAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_send_bol',
  });
  const hasDeleteLoadAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_delete',
  });
  const hasMarkAsDeliveredAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_mark_as_delivered',
  });
  const hasMarkAsPickedUpAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_mark_as_picked_up',
  });

  const hasNotAccess =
    hasNotManageAccess &&
    hasNotMarkAsPaidAccess &&
    hasNoSendInvoiceAccess &&
    hasNoSendBOLAccess &&
    hasDeleteLoadAccess &&
    hasMarkAsDeliveredAccess &&
    hasMarkAsPickedUpAccess;

  const [primaryAction, menuOptions] = useMemo(() => {
    let nextPrimaryAction: ReactNode = null;
    let nextMenuOptions: ReactNode[] = [];
    let idx = 0;

    function isFeatureToggleEnabled(feature: FeatureToggle) {
      return isFlagEnabled(settings?.features ?? null, feature);
    }

    for (const { options, isVisible: isVisibleGroup } of actions) {
      const menuProps: LoadsPageLoadActionProps = {
        load,
        settings,
        params,
        isMobile,
        isFeatureToggleEnabled,
      };
      const groupOptions: ReactNode[] = [];

      if (isVisibleGroup && !isVisibleGroup(menuProps)) {
        continue;
      }

      for (const {
        label: labelOrFn,
        action,
        isPrimary,
        isVisible,
        isDisabled,
        onClick,
        buttonProps,
        dataIntercomTarget,
        permission,
        endIcon,
        startIcon,
      } of options) {
        if (isVisible && !isVisible(menuProps)) {
          continue;
        }

        const label =
          typeof labelOrFn === 'function' ? labelOrFn(menuProps) : labelOrFn;
        const disabled = isDisabled?.(menuProps);
        const handleClick = () => {
          onClick?.(menuProps);

          if (action) {
            handleAction(action(menuProps));
          }
        };

        if (isPrimary?.(menuProps) && !isMobile) {
          nextPrimaryAction = permission ? (
            <PermissionButton
              {...buttonProps?.(menuProps)}
              name="loads"
              permission={permission}
              disabled={disabled}
              onClick={handleClick}
              data-intercom-target={dataIntercomTarget}
            >
              {label}
            </PermissionButton>
          ) : (
            <Button
              {...buttonProps?.(menuProps)}
              disabled={disabled}
              onClick={handleClick}
              data-intercom-target={dataIntercomTarget}
              startIcon={startIcon}
            >
              {label}
            </Button>
          );
        } else {
          const menuItem = (
            <MenuItem key={idx++} disabled={disabled} onClick={handleClick}>
              <Inline space="xsmall" verticalAlign="center">
                {label}
                {endIcon}
              </Inline>
            </MenuItem>
          );

          groupOptions.push(
            permission ? (
              <PermissionAction
                key={idx++}
                name="loads"
                permission={permission}
              >
                {menuItem}
              </PermissionAction>
            ) : (
              menuItem
            ),
          );
        }
      }

      if (groupOptions.length) {
        if (nextMenuOptions.length > 0) {
          nextMenuOptions.push(<Divider key={idx++} />);
        }

        nextMenuOptions.push(groupOptions);
      }
    }

    return [nextPrimaryAction, nextMenuOptions];
  }, [load, settings, params, isMobile, handleAction]);

  return (
    <Inline space="xsmall" verticalAlign="center">
      {primaryAction}

      {!!menuOptions.length &&
        !shouldShowOptions &&
        (hasNotAccess ? (
          <PermissionTooltip>
            <MenuButton
              variant="neutral"
              data-intercom-target="load-actions-menu"
              startIcon={hasNotAccess && <Lock />}
              renderMenu={(menuProps) => (
                <Menu
                  {...menuProps}
                  onClick={(event) => {
                    if (isHTMLElement(event.target)) {
                      menuProps.onClose();
                    }
                  }}
                >
                  {menuOptions}
                </Menu>
              )}
            >
              {isMobile ? <MoreHoriz /> : 'Options'}
            </MenuButton>
          </PermissionTooltip>
        ) : (
          <MenuButton
            variant="neutral"
            data-intercom-target="load-actions-menu"
            startIcon={hasNotAccess && <Lock />}
            renderMenu={(menuProps) => (
              <Menu
                {...menuProps}
                onClick={(event) => {
                  if (isHTMLElement(event.target)) {
                    menuProps.onClose();
                  }
                }}
              >
                {menuOptions}
              </Menu>
            )}
          >
            {isMobile ? <MoreHoriz /> : 'Options'}
          </MenuButton>
        ))}

      {load.can_be_edited && !isMobile && (
        <PermissionButton
          name="loads"
          variant="neutral"
          permission="can_manage"
          onClick={() => {
            navigate(
              parseURITemplate('/loads/{guid}/edit{?next}', {
                guid: load.guid,
                next: window.location.pathname + window.location.search,
              }),
            );
            trackLoadsEvent({
              name: 'Carrier Clicked Edit Load',
              page: 'loads_list',
              is_created_by_broker: !!load.is_created_by_broker,
              load_status: formatLoadStatus(load.status),
              utm_medium:
                params.stage === 'active'
                  ? 'Search Results'
                  : formatLoadStage(params.stage),
            });
          }}
        >
          Edit
        </PermissionButton>
      )}

      {!!menuOptions.length &&
        shouldShowOptions &&
        (hasNotAccess ? (
          <PermissionTooltip>
            <MenuButton
              variant="neutral"
              data-intercom-target="load-actions-menu"
              startIcon={hasNotAccess && <Lock />}
              renderMenu={(menuProps) => (
                <Menu
                  {...menuProps}
                  onClick={(event) => {
                    if (isHTMLElement(event.target)) {
                      menuProps.onClose();
                    }
                  }}
                >
                  {menuOptions}
                </Menu>
              )}
            >
              <MoreHoriz />
            </MenuButton>
          </PermissionTooltip>
        ) : (
          <MenuButton
            variant="neutral"
            data-intercom-target="load-actions-menu"
            startIcon={hasNotAccess && <Lock />}
            renderMenu={(menuProps) => (
              <Menu
                {...menuProps}
                onClick={(event) => {
                  if (isHTMLElement(event.target)) {
                    menuProps.onClose();
                  }
                }}
              >
                {menuOptions}
              </Menu>
            )}
          >
            <MoreHoriz />
          </MenuButton>
        ))}
    </Inline>
  );
}
